import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from "@angular/forms";
import { Router } from "@angular/router";
import { CustomerFeedbackApiService } from "@shared/services/customer-feedback.api.service";
import { CustomerFeedback } from "@shared/models/CustomerFeedback";
import { IdentityStoreService } from "@modules/identity/store/services/identity-store.service";

@Component({
  selector: 'customer-feedback',
  templateUrl: './customer-feedback.component.html',
  styleUrls: ['./customer-feedback.component.scss']
})
export class CustomerFeedbackComponent implements OnInit {
  isFeedbackSent: boolean = false;
  customerFeedbackForm: FormGroup;

  constructor(private formBuilder: FormBuilder,
              private identityStoreService: IdentityStoreService,
              private router: Router,
              private customerFeedbackApiService: CustomerFeedbackApiService,
  ) { }

  ngOnInit(): void {
    this.createCustomerFeedbackForm();

    this.identityStoreService.getUserProfile().pipe(
    ).subscribe(
      (userProfile) => {
        this.customerFeedbackForm.patchValue({email: userProfile?.email});
      }
    );
  }

  private createCustomerFeedbackForm() {
    this.customerFeedbackForm = this.formBuilder.group({
        isFound: [ null ],
        description: [ '', [Validators.required, this.noWhitespaceValidator] ],
        email: [ null, [
          Validators.required, 
          this.strictEmailValidator
        ] ]
      }
    );
  }

  /**
   * Custom validator that ensures the field is not empty or whitespace-only
   */
  private noWhitespaceValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }
    
    if (control.value.trim() === '') {
      return { 'whitespace': true };
    }
    
    return null;
  }

  /**
   * Custom email validator that requires a proper email format with TLD
   * Example: user@example.com (valid)
   * Example: user@example (invalid - missing TLD)
   * Example: akib@gmail (invalid - missing TLD)
   */
  private strictEmailValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const email = control.value.trim();
    
    // Stricter email pattern that requires:
    // - Local part (before @): letters, numbers, dots, hyphens, underscores, plus signs, percent
    // - @ symbol
    // - Domain name: letters, numbers, dots, hyphens
    // - Dot (.)
    // - TLD: at least 2 characters (letters only)
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailPattern.test(email)) {
      return { 'email': true };
    }
    
    return null;
  }

  public submitCustomerFeedbackForm() {
    // Mark all fields as touched to show validation errors
    this.customerFeedbackForm.markAllAsTouched();
    
    // Check if form is invalid
    if (this.customerFeedbackForm.invalid) {
      return;
    }
    
    // Explicitly check email field validity - ensure email is required and valid format
    const emailControl = this.customerFeedbackForm.get('email');
    if (!emailControl) {
      return;
    }
    
    // Check if email is required but empty
    if (!emailControl.value || emailControl.value.trim() === '') {
      emailControl.setErrors({ 'required': true });
      emailControl.markAsTouched();
      return;
    }
    
    // Validate email format with stricter pattern (must include TLD)
    const emailValue = emailControl.value?.trim();
    if (emailValue) {
      // Stricter email pattern requiring TLD
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(emailValue)) {
        emailControl.setErrors({ 'email': true });
        emailControl.markAsTouched();
        return;
      }
    }
    
    // Check if email format is invalid
    if (emailControl.invalid || emailControl.errors?.['email']) {
      emailControl.markAsTouched();
      return;
    }
    
    // Only proceed if email is valid
    if (!emailControl.valid) {
      return;
    }
    
    const pageLink = this.router.url;
    const feedback: CustomerFeedback = {...this.customerFeedbackForm.getRawValue(), pageLink};
    this.customerFeedbackApiService.addCustomerFeedback(feedback).subscribe(
      (response) => {
        this.isFeedbackSent = true;
      }
    );
  }

  clearFeedback() {
    this.customerFeedbackForm.get('description').setValue('');
  }

  isFormValid(): boolean {
    const emailControl = this.customerFeedbackForm.get('email');
    const descriptionControl = this.customerFeedbackForm.get('description');
    
    // Check if email is valid and not empty
    const isEmailValid = emailControl && emailControl.valid && emailControl.value && emailControl.value.trim() !== '';
    
    // Check if description is not blank
    const isDescriptionValid = descriptionControl && descriptionControl.value && descriptionControl.value.trim() !== '';
    
    return isEmailValid && isDescriptionValid;
  }

  shouldShowEmailTooltip(): boolean {
    const emailControl = this.customerFeedbackForm?.get('email');
    if (!emailControl) return false;

    const rawValue = emailControl.value ?? '';
    const value = String(rawValue).trim();

    // Show tooltip when email is missing or invalid
    return value === '' || emailControl.invalid;
  }
}
